import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  Box,
  Button,
  Grid,
  Paper,
  IconButton,
  Typography,
  Skeleton,
  Alert,
  AlertTitle,
  Table,
  Popover,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
} from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  Sector,
  Label,
} from "recharts";

// At the top of your component
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import Slider from "react-slick";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import LegendToggleIcon from "@mui/icons-material/LegendToggle";
import CountUp from "react-countup";
import PeopleIcon from "@mui/icons-material/People";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import EventNoteIcon from "@mui/icons-material/EventNote";
import ScienceIcon from "@mui/icons-material/Science";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import { BASE_URL, PIE_BG_COLORS, PIE_COLORS } from "../../constants";
import { ArrowRight } from "@mui/icons-material";
import BarChartIcon from "@mui/icons-material/BarChart";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { authState } from "../../recoil/authAtom";
import { useRecoilValue } from "recoil";

const settings = {
  dots: true,
  infinite: true,
  speed: 600,
  slidesToShow: 1,
  slidesToScroll: 1,
  arrows: false,
};
// Style
const OverallCardStyle = {
  p: 2,
  borderRadius: 3,
  backgroundColor: "#334155",
  color: "#fff",
  boxShadow: "0px 4px 10px rgba(0,0,0,0.05)",
};
const BarPieCardStyle = {
  p: 2,
  borderRadius: 3,
  backgroundColor: "#fff",
  boxShadow: "0px 4px 10px rgba(0,0,0,0.05)",
  height: "100%",
};

// Icon selector
const getIcon = (label) => {
  const lower = label.toLowerCase();
  if (
    lower.includes("revenue") ||
    lower.includes("collected") ||
    lower.includes("payout") ||
    lower.includes("balance")
  )
    return CurrencyRupeeIcon;
  if (lower.includes("test") || lower.includes("header")) return ScienceIcon;
  if (
    lower.includes("patient") ||
    lower.includes("centre") ||
    lower.includes("invoice")
  )
    return PeopleIcon;
  if (lower.includes("pending")) return PendingActionsIcon;
  return EventNoteIcon;
};

const toYMD = (date) => {
  const istString = date.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  const [day, month, year] = istString.split("/");
  return `${year}-${month}-${day}`;
};

const today = new Date();

const startOfToday = toYMD(
  new Date(today.getFullYear(), today.getMonth(), today.getDate()),
);
const endOfToday = startOfToday;

// // This Week
// const startOfThisWeekDate = new Date(today);
// startOfThisWeekDate.setDate(today.getDate() - today.getDay());
// const startOfThisWeek = toYMD(startOfThisWeekDate);

// const endOfThisWeekDate = new Date(startOfThisWeekDate);
// endOfThisWeekDate.setDate(startOfThisWeekDate.getDate() + 6);
// const endOfThisWeek = toYMD(endOfThisWeekDate);

// // Last Week
// const startOfLastWeekDate = new Date(startOfThisWeekDate);
// startOfLastWeekDate.setDate(startOfThisWeekDate.getDate() - 7);
// const startOfLastWeek = toYMD(startOfLastWeekDate);

// const endOfLastWeekDate = new Date(startOfThisWeekDate);
// endOfLastWeekDate.setDate(startOfThisWeekDate.getDate() - 1);
// const endOfLastWeek = toYMD(endOfLastWeekDate);
// ---------------- This Week (Monday → Sunday) ----------------
const startOfThisWeekDate = new Date(today);
const day = today.getDay(); // 0=Sunday, 1=Monday, ..., 6=Saturday

// If Sunday (0), shift back 6 days, else shift back (day-1)
startOfThisWeekDate.setDate(today.getDate() - (day === 0 ? 6 : day - 1));
const startOfThisWeek = toYMD(startOfThisWeekDate);

const endOfThisWeekDate = new Date(startOfThisWeekDate);
endOfThisWeekDate.setDate(startOfThisWeekDate.getDate() + 6);
const endOfThisWeek = toYMD(endOfThisWeekDate);

// ---------------- Last Week (Monday → Sunday) ----------------
const startOfLastWeekDate = new Date(startOfThisWeekDate);
startOfLastWeekDate.setDate(startOfThisWeekDate.getDate() - 7);
const startOfLastWeek = toYMD(startOfLastWeekDate);

const endOfLastWeekDate = new Date(startOfThisWeekDate);
endOfLastWeekDate.setDate(startOfThisWeekDate.getDate() - 1);
const endOfLastWeek = toYMD(endOfLastWeekDate);

// This Month
const startOfThisMonth = toYMD(
  new Date(today.getFullYear(), today.getMonth(), 1),
);
const endOfThisMonth = toYMD(
  new Date(today.getFullYear(), today.getMonth() + 1, 0),
);

// Last Month
const startOfLastMonth = toYMD(
  new Date(today.getFullYear(), today.getMonth() - 1, 1),
);
const endOfLastMonth = toYMD(
  new Date(today.getFullYear(), today.getMonth(), 0),
);

const filterOptions = [
  {
    label: "Today",
    value: "today",
    startDate: startOfToday,
    endDate: endOfToday,
  },
  {
    label: "This Week",
    value: "thisWeek",
    startDate: startOfThisWeek,
    endDate: endOfThisWeek,
  },
  {
    label: "Last Week",
    value: "lastWeek",
    startDate: startOfLastWeek,
    endDate: endOfLastWeek,
  },
  {
    label: "This Month",
    value: "thisMonth",
    startDate: startOfThisMonth,
    endDate: endOfThisMonth,
  },
  {
    label: "Last Month",
    value: "lastMonth",
    startDate: startOfLastMonth,
    endDate: endOfLastMonth,
  },
];

const DashboardInformation = () => {
  const user = useRecoilValue(authState);
  const [selectedFilter, setSelectedFilter] = useState(filterOptions[0]);
  const [customDate, setCustomDate] = useState(null);
  const [kpis, setKpis] = useState([]);
  const [deptPieData, setDeptPieData] = useState([]);
  const [patientPieData, setPatientPieData] = useState([]);
  const [loading, setLoading] = useState(true);

  const groupedKPIs = {
    top: [],
    pathologyRadiology: [],
    pending: [],
    opd: [],
    bottom: [],
  };

  kpis.forEach((kpi) => {
    if (groupedKPIs[kpi.section]) {
      groupedKPIs[kpi.section].push(kpi);
    }
  });

  const [dashInfo, setDashInfo] = useState(null);
  const [doctorWiseTestsData, setDoctorWiseTestsData] = useState([]);
  const [testTypes, setTestTypes] = useState([]);
  const getDashInfo = async (start, end) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${BASE_URL}api/user/common/dashboard-information?startDate=${start}&endDate=${end}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      const data = await response.json();
      console.log("response:", data);

      // if (data.success) {
      //   setDashInfo(data.dashInfo);
      //   if (data?.dashInfo?.doctorWiseTests) {
      //     const transformed = Object.entries(
      //       data?.dashInfo?.doctorWiseTests
      //     ).map(([doctor, tests]) => ({
      //       doctor,
      //       ...tests,
      //     }));
      //     setDoctorWiseTestsData(transformed);

      //     const testTypes = [
      //       ...new Set(
      //         Object.values(data?.dashInfo?.doctorWiseTests || {}).flatMap(
      //           (tests) => Object.keys(tests)
      //         )
      //       ),
      //     ];
      //     setTestTypes(testTypes);
      //   }
      // }
      if (data.success) {
        setDashInfo(data.dashInfo);

        if (data?.dashInfo?.doctorWiseTests) {
          const transformedForChart = Object.entries(
            data.dashInfo.doctorWiseTests,
          ).map(([doctor, tests]) => {
            const obj = { doctor };
            tests.forEach((t) => {
              obj[t.testName] = t.count;
            });
            return obj;
          });

          setDoctorWiseTestsData(transformedForChart);

          // Collect test types in order of first appearance across doctors
          const testTypes = [];
          const added = new Set();
          Object.values(data.dashInfo.doctorWiseTests).forEach((tests) => {
            tests.forEach((t) => {
              if (!added.has(t.testName)) {
                added.add(t.testName);
                testTypes.push(t.testName);
              }
            });
          });

          setTestTypes(testTypes);
        }
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };
  console.log("DDDFFF111", doctorWiseTestsData);
  console.log("DDDFFF222", testTypes);
  useEffect(() => {
    if (selectedFilter)
      getDashInfo(selectedFilter?.startDate, selectedFilter?.endDate);
  }, [selectedFilter]);

  // const CustomTooltipStack = ({ active, payload, label }) => {
  //   if (active && payload && payload.length) {
  //     return (
  //       <Paper
  //         elevation={3}
  //         sx={{
  //           p: 1.5,
  //           minWidth: 160,
  //           backgroundColor: "#fafafa",
  //           border: "1px solid #ddd",
  //           borderRadius: 1.5,
  //         }}
  //       >
  //         <div
  //           style={{
  //             fontWeight: "bold",
  //             marginBottom: 4,
  //             color: "#111827",
  //           }}
  //         >
  //           {label}
  //         </div>
  //         {payload.map((entry, idx) => (
  //           <div
  //             key={entry.name + idx}
  //             style={{
  //               display: "flex",
  //               justifyContent: "space-between",
  //               alignItems: "center",
  //               marginBottom: 4,
  //             }}
  //           >
  //             <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
  //               <span
  //                 style={{
  //                   display: "inline-block",
  //                   width: 10,
  //                   height: 10,
  //                   borderRadius: "50%",
  //                   backgroundColor: entry.fill,
  //                 }}
  //               ></span>
  //               <span style={{ color: "#111827", fontSize: 12 }}>
  //                 {entry.name}
  //               </span>
  //             </div>
  //             <span style={{ color: "#111827", fontSize: 12 }}>
  //               {entry.value}
  //             </span>
  //           </div>
  //         ))}
  //       </Paper>
  //     );
  //   }
  //   return null;
  // };

  const CustomTooltipStack = ({ active, payload, label }) => {
    const scrollRef = useRef(null);

    useEffect(() => {
      if (payload && payload.length > 10 && scrollRef.current) {
        const scrollContainer = scrollRef.current;
        let scrollAmount = 0;
        let scrollInterval;

        // wait 2 seconds before starting scroll
        const timeout = setTimeout(() => {
          scrollInterval = setInterval(() => {
            if (
              scrollContainer.scrollTop + scrollContainer.clientHeight >=
              scrollContainer.scrollHeight
            ) {
              scrollAmount = 0; // reset scroll to top once reached bottom
              scrollContainer.scrollTop = 0;
            } else {
              scrollAmount += 1; // scroll 1px at a time
              scrollContainer.scrollTop = scrollAmount;
            }
          }, 40); // speed of scrolling (lower = faster)
        }, 2000);

        return () => {
          clearTimeout(timeout);
          clearInterval(scrollInterval);
        };
      }
    }, [payload]);

    if (active && payload && payload.length) {
      return (
        <Paper
          elevation={3}
          sx={{
            p: 1.5,
            minWidth: 160,
            maxHeight: 400, // fixed height
            backgroundColor: "#fafafa",
            border: "1px solid #ddd",
            borderRadius: 1.5,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              fontWeight: "bold",
              marginBottom: 4,
              color: "#111827",
            }}
          >
            {label}
          </div>

          {/* Scrollable area */}
          <div
            ref={scrollRef}
            style={{
              maxHeight: 200, // keeps ~10 rows visible
              overflowY: payload.length > 10 ? "auto" : "visible",
              paddingRight: 8, // ✅ add space so scrollbar doesn't overlap content
            }}
          >
            {payload.map((entry, idx) => (
              <div
                key={entry.name + idx}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 4,
                  paddingRight: 4, // ✅ small buffer for value text
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span
                    style={{
                      display: "inline-block",
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      backgroundColor: entry.fill,
                    }}
                  ></span>
                  <span style={{ color: "#111827", fontSize: 12 }}>
                    {entry.name}
                  </span>
                </div>
                <span
                  style={{
                    color: "#111827",
                    fontSize: 12,
                    whiteSpace: "nowrap", // ✅ prevent value from wrapping
                  }}
                >
                  {entry.value}
                </span>
              </div>
            ))}
          </div>
        </Paper>
      );
    }
    return null;
  };

  const [openIndex, setOpenIndex] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [chartType, setChartType] = useState(null);

  const handlePopoverOpen = (event, index, type) => {
    const target = event;
    console.log("E", event);
    setAnchorEl(event);
    setOpenIndex(index);
    setChartType(type);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setOpenIndex(null);
    setChartType(null);
  };
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [rangeDate, setRangeDate] = useState(false);

  console.log("SD", startDate);
  const getRangeData = async () => {
    setRangeDate(true);
    setSelectedFilter(null);
    getDashInfo(startDate, endDate);
  };
  const renderKpiCards = (title, data, columns = 4) => {
    return (
      <Box mb={2}>
        <Grid container spacing={2}>
          {(loading ? Array.from({ length: columns }) : data).map((item, i) => (
            <Grid item xs={12} sm={6} md={12 / columns} key={item?.label || i}>
              <Paper
                sx={{
                  ...OverallCardStyle,
                  backgroundColor:
                    title === "Overall Summary"
                      ? "#334155"
                      : title === "Pending Items"
                        ? "#fff8f1"
                        : "#fff",
                  color:
                    title === "Overall Summary"
                      ? "#fff"
                      : title === "Pending Items"
                        ? "#b45309"
                        : "",
                  border: title === "Pending Items" ? "2px solid #f97316" : "",
                }}
              >
                <Box display="flex" alignItems="center" gap={2}>
                  {loading ? (
                    <Skeleton variant="circular" width={40} height={40} />
                  ) : (
                    React.createElement(getIcon(item.label), {
                      fontSize: "large",
                      sx: { color: item.color },
                    })
                  )}
                  <Box>
                    <Typography variant="subtitle2">
                      {loading ? <Skeleton width={120} /> : item.label}
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {loading ? (
                        <Skeleton width={60} />
                      ) : (
                        <CountUp
                          end={item.value}
                          duration={1.5}
                          separator=","
                        />
                      )}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  const [rangeValue, setRangeValue] = useState(10);
  const [rangeUnit, setRangeUnit] = useState("days");
  const [rangeData, setRangeData] = useState(null);
  const [rangeRevenueLoading, setRangeRevenueLoading] = useState(false);
  const getRangeRevenue = async (range) => {
    console.log("RRR", range);
    try {
      setRangeRevenueLoading(true);
      const response = await fetch(
        `${BASE_URL}api/user/common/dashboard-range-revenue?rangeValue=${rangeValue}&rangeUnit=${rangeUnit}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      const resp = await response.json();
      if (resp.success) {
        setRangeData(resp?.data);
        // if (resp?.doctorWiseTests) {
        //   const transformed = Object.entries(resp?.doctorWiseTests).map(
        //     ([doctor, tests]) => ({
        //       doctor,
        //       ...tests,
        //     })
        //   );
        //   setDoctorWiseTestsData(transformed);

        //   const testTypes = [
        //     ...new Set(
        //       Object.values(resp?.doctorWiseTests || {}).flatMap((tests) =>
        //         Object.keys(tests)
        //       )
        //     ),
        //   ];
        //   setTestTypes(testTypes);
        // }
      }
    } catch (err) {
    } finally {
      setRangeRevenueLoading(false);
    }
  };

  const [value, setValue] = React.useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  useEffect(() => {
    const today = new Date();
    const endDate = new Date(today); // today (inclusive)
    const startDate = new Date(today);

    if (rangeUnit === "days") {
      startDate.setDate(today.getDate() - rangeValue + 1); // inclusive
    } else if (rangeUnit === "months") {
      startDate.setMonth(today.getMonth() - rangeValue);
      startDate.setDate(startDate.getDate() + 1); // ensure inclusion
    }

    const formatDate = (date) => date.toISOString().split("T")[0]; // "YYYY-MM-DD"

    const range = {
      from: formatDate(startDate),
      to: formatDate(endDate),
    };
    getRangeRevenue(range);
  }, [rangeValue, rangeUnit]);
  const indianFormatter = useCallback(
    (value) => value.toLocaleString("en-IN"),
    [],
  );
  const [animate, setAnimate] = useState(true);
  useEffect(() => {
    setAnimate(false); // disable animation after first render
  }, []);
  const renderActiveShape = (props) => {
    const {
      cx,
      cy,
      midAngle,
      innerRadius,
      outerRadius,
      startAngle,
      endAngle,
      fill,
      payload,
      percent,
      value,
    } = props;

    const RADIAN = Math.PI / 180;
    const sin = Math.sin(-RADIAN * (midAngle || 1));
    const cos = Math.cos(-RADIAN * (midAngle || 1));
    const sx = (cx || 0) + ((outerRadius || 0) + 3) * cos;
    const sy = (cy || 0) + ((outerRadius || 0) + 3) * sin;
    const mx = (cx || 0) + ((outerRadius || 0) + 13) * cos;
    const my = (cy || 0) + ((outerRadius || 0) + 13) * sin;
    // const ex = mx + (cos >= 0 ? 1 : -1) * 22;
    // const ey = my;
    const ex = mx + 10 * cos;
    const ey = my + 10 * sin;

    const textAnchor = cos >= 0 ? "start" : "end";

    return (
      <g>
        {/* <text x={cx} y={cy} dy={8} textAnchor="middle" fill={fill}>
          {payload.name}
        </text> */}
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={(outerRadius || 0) + 6}
          outerRadius={(outerRadius || 0) + 10}
          fill={fill}
        />
        <path
          d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
          stroke={fill}
          fill="none"
        />
        <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
        {/* <text
          x={ex + (cos >= 0 ? 1 : -1) * 12}
          y={ey}
          textAnchor={textAnchor}
          fill="#333"
        >
          {`PV ${value}`}
        </text> */}
        <text
          x={ex + (cos >= 0 ? 1 : -1) * 12}
          y={ey}
          dy={18}
          textAnchor={textAnchor}
          fill="#999"
        >
          {`${(percent * 100).toFixed(2)}%`}
        </text>
      </g>
    );
  };
  const renderChartCard = () => {
    const barData = rangeData && rangeData;

    const CustomTooltip = ({ active, payload, label }) => {
      if (active && payload && payload.length) {
        const { revenue, invoices } = payload[0].payload;
        return (
          <Paper
            elevation={3}
            sx={{
              p: 1.5,
              minWidth: 160,
              backgroundColor: "#fafafa",
              border: "1px solid #ddd",
              borderRadius: 1.5,
            }}
          >
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {label}
            </Typography>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="#2a9d8f">
                Revenue:
              </Typography>
              <Typography variant="body2" fontWeight="bold" color="#2a9d8f">
                ₹{revenue}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="#e76f51">
                Invoices:
              </Typography>
              <Typography variant="body2" fontWeight="bold" color="#e76f51">
                {invoices}
              </Typography>
            </Box>
          </Paper>
        );
      }
      return null;
    };

    function TabPanel(props) {
      const { children, value, index, ...other } = props;

      return (
        <div role="tabpanel" hidden={value !== index} {...other}>
          {value === index && (
            <Box sx={{ p: 2 }}>
              <Typography>{children}</Typography>
            </Box>
          )}
        </div>
      );
    }

    return (
      <Box mb={3}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            {/* Bar graph */}
            <Paper sx={{ ...BarPieCardStyle }}>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                mb={5}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  fontFamily: "Inter, Arial, sans-serif",
                  color: "#111827",
                }}
              >
                <span style={{ fontSize: 14 }}>Last</span>

                <select
                  value={rangeValue}
                  onChange={(e) => setRangeValue(Number(e.target.value))}
                  style={{
                    borderRadius: "6px",
                    backgroundColor: "#fff",
                    color: "#111827",
                    border: "1px solid #d1d5db",
                    fontSize: "14px",
                    padding: "4px 8px",
                    outline: "none",
                    cursor: "pointer",
                  }}
                >
                  {[5, 10, 15, 20, 25, 30].map((num) => (
                    <option key={num} value={num}>
                      {num}
                    </option>
                  ))}
                </select>

                <select
                  value={rangeUnit}
                  onChange={(e) => setRangeUnit(e.target.value)}
                  style={{
                    borderRadius: "6px",
                    backgroundColor: "#fff",
                    color: "#111827",
                    border: "1px solid #d1d5db",
                    fontSize: "14px",
                    padding: "4px 8px",
                    outline: "none",
                    cursor: "pointer",
                  }}
                >
                  <option value="days">Days</option>
                  <option value="weeks">Weeks</option>
                  <option value="months">Months</option>
                </select>

                <span style={{ fontSize: 14 }}>Revenue</span>
              </Typography>

              {rangeRevenueLoading ? (
                <Skeleton variant="rectangular" height={260} />
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={barData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 13 }} />
                    <YAxis tick={{ fontSize: 13 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="revenue" name="Revenue" fill="#2a9d8f" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Paper>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
            sx={{ display: { xs: "none", md: "block" } }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Tabs
                value={value}
                onChange={handleChange}
                variant="scrollable" // 👈 allow horizontal scroll
                scrollButtons="auto" // 👈 show scroll buttons when overflow
                textColor="primary"
                indicatorColor="primary"
                TabIndicatorProps={{
                  style: { backgroundColor: "#1976d2", height: "2px" },
                }}
                sx={{
                  minHeight: "32px",
                  width: "200px", // 👈 fixed width for tabs container
                  overflowX: "auto", // 👈 enable scrolling
                  flexShrink: 0,
                }}
              >
                <Tab
                  label="Revenue"
                  sx={{
                    fontSize: "10px",
                    padding: "0px 8px",
                    minHeight: "32px",
                    textTransform: "none",
                    borderRadius: "8px 8px 0 0",
                    "&.Mui-selected": {
                      color: "#1976d2",
                      backgroundColor: "#e3f2fd",
                    },
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                    minWidth: "20px",
                    px: 2,
                  }}
                />
                <Tab
                  label="OPD"
                  sx={{
                    fontSize: "10px",
                    padding: "0px 8px",
                    minHeight: "32px",
                    textTransform: "none",
                    borderRadius: "8px 8px 0 0",
                    "&.Mui-selected": {
                      color: "#1976d2",
                      backgroundColor: "#e3f2fd",
                    },
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                    minWidth: "20px",
                    px: 2,
                  }}
                />
                <Tab
                  label="Tests"
                  sx={{
                    fontSize: "10px",
                    padding: "0px 8px",
                    minHeight: "32px",
                    textTransform: "none",
                    borderRadius: "8px 8px 0 0",
                    "&.Mui-selected": {
                      color: "#1976d2",
                      backgroundColor: "#e3f2fd",
                    },
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                    minWidth: "20px",
                    px: 2,
                  }}
                />
                {/* You can add more tabs here, and they’ll scroll horizontally */}
              </Tabs>

              <Box>
                <Chip
                  label={
                    selectedFilter
                      ? selectedFilter.label
                      : `${new Date(startDate)
                          .toLocaleDateString("en-GB", {
                            day: "numeric",
                            month: "short",
                            year: "2-digit",
                          })
                          .replace(" ", "'")} To ${new Date(endDate)
                          .toLocaleDateString("en-GB", {
                            day: "numeric",
                            month: "short",
                            year: "2-digit",
                          })
                          .replace(" ", "'")}`
                  }
                />
              </Box>
            </Box>

            <TabPanel value={value} index={0}>
              <Swiper
                modules={[Navigation, Pagination]}
                navigation
                pagination={{ clickable: true }}
                spaceBetween={10}
                slidesPerView={1}
                height={260}
                style={{
                  ...BarPieCardStyle,
                }}
              >
                {dashInfo?.revenuePie?.map((chart, index) => {
                  let pieData = Object.values(chart?.data || {}).map(
                    (item, i) => ({
                      name: item.name,
                      value: item.value,
                      color: PIE_COLORS[i % PIE_COLORS.length],
                    }),
                  );

                  if (pieData.length === 0) {
                    pieData = [
                      {
                        name: "No Data",
                        value: 1,
                        color: "#334155",
                      },
                    ];
                  }

                  return (
                    <SwiperSlide key={index} style={{ borderRadius: 3 }}>
                      <Paper
                        sx={{
                          ...BarPieCardStyle,
                          backgroundColor: PIE_BG_COLORS[index],
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="subtitle1" fontWeight="bold">
                            {chart?.title}
                          </Typography>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={(e) =>
                              handlePopoverOpen(
                                e.currentTarget,
                                index,
                                "revenue",
                              )
                            }
                          >
                            <LegendToggleIcon />
                          </IconButton>
                        </Box>

                        {loading ? (
                          <Skeleton variant="rectangular" height={260} />
                        ) : (
                          <ResponsiveContainer width="100%" height={260}>
                            <PieChart>
                              <Pie
                                data={pieData}
                                activeShape={renderActiveShape}
                                cx="50%"
                                cy="50%"
                                innerRadius={60}
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="value"
                                isAnimationActive={animate}
                              >
                                {pieData.map((entry, idx) => (
                                  <Cell key={idx} fill={entry.color} />
                                ))}
                                <Label
                                  value="Total"
                                  position="center"
                                  dy={-12} // shift upwards
                                  style={{
                                    fontSize: "13px",
                                    fill: "#475569",
                                  }}
                                />
                                <Label
                                  value={
                                    "₹ " +
                                    pieData
                                      .reduce(
                                        (sum, item) =>
                                          sum +
                                          (item.name !== "No Data"
                                            ? item.value
                                            : 0),
                                        0,
                                      )
                                      .toLocaleString("en-IN")
                                  }
                                  position="center"
                                  dy={10}
                                  style={{
                                    fontSize: "16px",
                                    fontWeight: "bold",
                                    fill: "#1e293b",
                                  }}
                                />
                              </Pie>

                              <Tooltip
                                content={({ active, payload }) => {
                                  if (active && payload && payload.length) {
                                    const { name, value } = payload[0].payload;
                                    if (name === "No Data") return null;

                                    const displayValue = `₹ ${value.toLocaleString(
                                      "en-IN",
                                    )}`;

                                    return (
                                      <div
                                        style={{
                                          background:
                                            "rgba(255, 255, 255, 0.8)",
                                          border: "1px solid #ccc",
                                          padding: "8px",
                                          borderRadius: "4px",
                                          fontSize: "13px",
                                        }}
                                      >
                                        <strong>{name}:</strong>
                                        {displayValue}
                                      </div>
                                    );
                                  }
                                  return null;
                                }}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        )}
                      </Paper>
                    </SwiperSlide>
                  );
                })}
              </Swiper>
            </TabPanel>
            <TabPanel value={value} index={1}>
              <Swiper
                modules={[Navigation, Pagination]}
                navigation
                pagination={{ clickable: true }}
                spaceBetween={10}
                slidesPerView={1}
                height={260}
                style={{
                  ...BarPieCardStyle,
                }}
              >
                {dashInfo?.opdCountsPie?.map((chart, index) => {
                  let pieData = Object.values(chart?.data || {}).map(
                    (item, i) => ({
                      name: item.name,
                      value: item.value,
                      color: PIE_COLORS[i % PIE_COLORS.length],
                    }),
                  );

                  if (pieData.length === 0) {
                    pieData = [
                      {
                        name: "No Data",
                        value: 1,
                        color: "#334155",
                      },
                    ];
                  }

                  return (
                    <SwiperSlide key={index} style={{ borderRadius: 3 }}>
                      <Paper
                        sx={{
                          ...BarPieCardStyle,
                          backgroundColor: PIE_BG_COLORS[index],
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="subtitle1" fontWeight="bold">
                            {chart?.title}
                          </Typography>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={(e) =>
                              handlePopoverOpen(e.currentTarget, index, "OPD")
                            }
                          >
                            <LegendToggleIcon />
                          </IconButton>
                        </Box>

                        {loading ? (
                          <Skeleton variant="rectangular" height={260} />
                        ) : (
                          <ResponsiveContainer width="100%" height={260}>
                            <PieChart>
                              <Pie
                                data={pieData}
                                activeShape={renderActiveShape}
                                cx="50%"
                                cy="50%"
                                innerRadius={60}
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="value"
                                isAnimationActive={animate}
                              >
                                {pieData.map((entry, idx) => (
                                  <Cell key={idx} fill={entry.color} />
                                ))}
                                <Label
                                  value="Total"
                                  position="center"
                                  dy={-12} // shift upwards
                                  style={{
                                    fontSize: "13px",
                                    fill: "#475569",
                                  }}
                                />
                                <Label
                                  value={pieData.reduce(
                                    (sum, item) =>
                                      sum +
                                      (item.name !== "No Data"
                                        ? item.value
                                        : 0),
                                    0,
                                  )}
                                  position="center"
                                  dy={10}
                                  style={{
                                    fontSize: "16px",
                                    fontWeight: "bold",
                                    fill: "#1e293b",
                                  }}
                                />
                              </Pie>

                              <Tooltip
                                content={({ active, payload }) => {
                                  if (active && payload && payload.length) {
                                    const { name, value } = payload[0].payload;
                                    if (name === "No Data") return null;

                                    const displayValue =
                                      value.toLocaleString("en-IN");

                                    return (
                                      <div
                                        style={{
                                          background:
                                            "rgba(255, 255, 255, 0.8)",
                                          border: "1px solid #ccc",
                                          padding: "8px",
                                          borderRadius: "4px",
                                          fontSize: "13px",
                                        }}
                                      >
                                        <strong>{name}:</strong> {displayValue}
                                      </div>
                                    );
                                  }
                                  return null;
                                }}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        )}
                      </Paper>
                    </SwiperSlide>
                  );
                })}
              </Swiper>
            </TabPanel>
            <TabPanel value={value} index={2}>
              <Swiper
                modules={[Navigation, Pagination]}
                navigation
                pagination={{ clickable: true }}
                spaceBetween={10}
                slidesPerView={1}
                height={260}
                style={{
                  ...BarPieCardStyle,
                }}
              >
                {dashInfo?.testCounts?.map((chart, index) => {
                  let pieData = Object.values(chart?.data || {}).map(
                    (item, i) => ({
                      name: item.name,
                      value: item.value,
                      color: PIE_COLORS[i % PIE_COLORS.length],
                    }),
                  );

                  if (pieData.length === 0) {
                    pieData = [
                      {
                        name: "No Data",
                        value: 1,
                        color: "#334155",
                      },
                    ];
                  }

                  return (
                    <SwiperSlide key={index} style={{ borderRadius: 3 }}>
                      <Paper
                        sx={{
                          ...BarPieCardStyle,
                          backgroundColor: PIE_BG_COLORS[index],
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="subtitle1" fontWeight="bold">
                            {chart?.title}
                          </Typography>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={(e) =>
                              handlePopoverOpen(e.currentTarget, index, "tests")
                            }
                          >
                            <LegendToggleIcon />
                          </IconButton>
                        </Box>

                        {loading ? (
                          <Skeleton variant="rectangular" height={260} />
                        ) : (
                          <ResponsiveContainer width="100%" height={260}>
                            <PieChart>
                              <Pie
                                data={pieData}
                                activeShape={renderActiveShape}
                                cx="50%"
                                cy="50%"
                                innerRadius={60}
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="value"
                                isAnimationActive={animate}
                              >
                                {pieData.map((entry, idx) => (
                                  <Cell key={idx} fill={entry.color} />
                                ))}
                                <Label
                                  value="Total"
                                  position="center"
                                  dy={-12} // shift upwards
                                  style={{
                                    fontSize: "13px",
                                    fill: "#475569",
                                  }}
                                />
                                <Label
                                  value={pieData.reduce(
                                    (sum, item) =>
                                      sum +
                                      (item.name !== "No Data"
                                        ? item.value
                                        : 0),
                                    0,
                                  )}
                                  position="center"
                                  dy={10}
                                  style={{
                                    fontSize: "16px",
                                    fontWeight: "bold",
                                    fill: "#1e293b",
                                  }}
                                />
                              </Pie>

                              <Tooltip
                                content={({ active, payload }) => {
                                  if (active && payload && payload.length) {
                                    const { name, value } = payload[0].payload;
                                    if (name === "No Data") return null;

                                    const displayValue =
                                      value.toLocaleString("en-IN");

                                    return (
                                      <div
                                        style={{
                                          background:
                                            "rgba(255, 255, 255, 0.8)",
                                          border: "1px solid #ccc",
                                          padding: "8px",
                                          borderRadius: "4px",
                                          fontSize: "13px",
                                        }}
                                      >
                                        <strong>{name}:</strong> {displayValue}
                                      </div>
                                    );
                                  }
                                  return null;
                                }}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        )}
                      </Paper>
                    </SwiperSlide>
                  );
                })}
              </Swiper>
            </TabPanel>
            {/* <Swiper
              modules={[Navigation, Pagination]}
              navigation
              pagination={{ clickable: true }}
              spaceBetween={10}
              slidesPerView={1}
              height={260}
              style={{
                ...BarPieCardStyle,
              }}
            >
              {dashInfo?.revenuePie?.map((chart, index) => {
                let pieData = Object.values(chart?.data || {}).map(
                  (item, i) => ({
                    name: item.name,
                    value: item.value,
                    color: PIE_COLORS[i % PIE_COLORS.length],
                  })
                );

                if (pieData.length === 0) {
                  pieData = [
                    {
                      name: "No Data",
                      value: 1,
                      color: "#334155",
                    },
                  ];
                }

                return (
                  <SwiperSlide key={index} style={{ borderRadius: 3 }}>
                    <Paper
                      sx={{
                        ...BarPieCardStyle,
                        backgroundColor: PIE_BG_COLORS[index],
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="subtitle1" fontWeight="bold">
                          {chart?.title}
                        </Typography>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={(e) => handlePopoverOpen(e, index)}
                        >
                          <LegendToggleIcon />
                        </IconButton>
                      </Box>

                      {loading ? (
                        <Skeleton variant="rectangular" height={260} />
                      ) : (
                        <ResponsiveContainer width="100%" height={260}>
                          <PieChart>
                            <Pie
                              data={pieData}
                              activeShape={renderActiveShape}
                              cx="50%"
                              cy="50%"
                              innerRadius={60}
                              outerRadius={80}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {pieData.map((entry, idx) => (
                                <Cell key={idx} fill={entry.color} />
                              ))}
                            </Pie>

                            <Tooltip
                              content={({ active, payload }) => {
                                if (active && payload && payload.length) {
                                  const { name, value } = payload[0].payload;
                                  if (name === "No Data") return null;

                                  const isOPD =
                                    chart?.title?.includes("OPD Count");
                                  const displayValue = isOPD
                                    ? value.toLocaleString("en-IN")
                                    : `₹${value.toLocaleString("en-IN")}`;

                                  return (
                                    <div
                                      style={{
                                        background: "rgba(255, 255, 255, 0.8)",
                                        border: "1px solid #ccc",
                                        padding: "8px",
                                        borderRadius: "4px",
                                        fontSize: "13px",
                                      }}
                                    >
                                      <strong>{name}:</strong> {displayValue}
                                    </div>
                                  );
                                }
                                return null;
                              }}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      )}
                    </Paper>
                  </SwiperSlide>
                );
              })}
            </Swiper>
            <Swiper
              modules={[Navigation, Pagination]}
              navigation
              pagination={{ clickable: true }}
              spaceBetween={10}
              slidesPerView={1}
              height={260}
              style={{
                ...BarPieCardStyle,
              }}
            >
              {dashInfo?.opdCountsPie?.map((chart, index) => {
                let pieData = Object.values(chart?.data || {}).map(
                  (item, i) => ({
                    name: item.name,
                    value: item.value,
                    color: PIE_COLORS[i % PIE_COLORS.length],
                  })
                );

                if (pieData.length === 0) {
                  pieData = [
                    {
                      name: "No Data",
                      value: 1,
                      color: "#334155",
                    },
                  ];
                }

                return (
                  <SwiperSlide key={index} style={{ borderRadius: 3 }}>
                    <Paper
                      sx={{
                        ...BarPieCardStyle,
                        backgroundColor: PIE_BG_COLORS[index],
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="subtitle1" fontWeight="bold">
                          {chart?.title}
                        </Typography>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={(e) => handlePopoverOpen(e, index)}
                        >
                          <LegendToggleIcon />
                        </IconButton>
                      </Box>

                      {loading ? (
                        <Skeleton variant="rectangular" height={260} />
                      ) : (
                        <ResponsiveContainer width="100%" height={260}>
                          <PieChart>
                            <Pie
                              data={pieData}
                              activeShape={renderActiveShape}
                              cx="50%"
                              cy="50%"
                              innerRadius={60}
                              outerRadius={80}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {pieData.map((entry, idx) => (
                                <Cell key={idx} fill={entry.color} />
                              ))}
                            </Pie>

                            <Tooltip
                              content={({ active, payload }) => {
                                if (active && payload && payload.length) {
                                  const { name, value } = payload[0].payload;
                                  if (name === "No Data") return null;

                                  const isOPD =
                                    chart?.title?.includes("OPD Count");
                                  const displayValue = isOPD
                                    ? value.toLocaleString("en-IN")
                                    : `₹${value.toLocaleString("en-IN")}`;

                                  return (
                                    <div
                                      style={{
                                        background: "rgba(255, 255, 255, 0.8)",
                                        border: "1px solid #ccc",
                                        padding: "8px",
                                        borderRadius: "4px",
                                        fontSize: "13px",
                                      }}
                                    >
                                      <strong>{name}:</strong> {displayValue}
                                    </div>
                                  );
                                }
                                return null;
                              }}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      )}
                    </Paper>
                  </SwiperSlide>
                );
              })}
            </Swiper> */}
          </Grid>
        </Grid>

        {dashInfo?.revenuePie?.map((chart, chartIndex) => (
          <Popover
            key={chartIndex}
            open={chartIndex === openIndex && chartType === "revenue"}
            onClose={handlePopoverClose}
            anchorReference="anchorPosition"
            anchorPosition={{ top: 10, left: window.innerWidth - 300 }}
            PaperProps={{
              sx: {
                position: "fixed",
                top: 10,
                right: 10,
                width: 280,
                boxShadow: 3,
              },
            }}
          >
            <Box>
              <Table sx={{ minWidth: 250 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {chart.title === "Doctor" && <strong>Doctor</strong>}
                      {chart.title === "Center" && <strong>Center</strong>}
                    </TableCell>
                    <TableCell align="right">
                      <strong>Revenue</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.values(chart?.data || {}).map((entry, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: "50%",
                              backgroundColor:
                                PIE_COLORS[index % PIE_COLORS.length],
                            }}
                          />
                          {entry.name}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        {/* {chart?.title === "Doctor OPD Count" ? "" : "₹"} */}
                        {`₹ ${entry.value.toLocaleString()}`}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Popover>
        ))}
        {dashInfo?.opdCountsPie?.map((chart, chartIndex) => (
          <Popover
            key={chartIndex}
            open={chartIndex === openIndex && chartType === "OPD"}
            onClose={handlePopoverClose}
            anchorReference="anchorPosition"
            anchorPosition={{ top: 10, left: window.innerWidth - 300 }}
            PaperProps={{
              sx: {
                position: "fixed",
                top: 10,
                right: 10,
                width: 280,
                boxShadow: 3,
              },
            }}
          >
            <Box>
              <Table sx={{ minWidth: 250 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {chart.title === "Doctor" && <strong>Doctor</strong>}
                      {chart.title === "Center" && <strong>Center</strong>}
                    </TableCell>
                    <TableCell align="right">
                      <strong>Count</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.values(chart?.data || {}).map((entry, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: "50%",
                              backgroundColor:
                                PIE_COLORS[index % PIE_COLORS.length],
                            }}
                          />
                          {entry.name}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        {entry.value.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Popover>
        ))}
        {dashInfo?.testCounts?.map((chart, chartIndex) => (
          <Popover
            key={chartIndex}
            open={chartIndex === openIndex && chartType === "tests"}
            onClose={handlePopoverClose}
            anchorReference="anchorPosition"
            anchorPosition={{ top: 10, left: window.innerWidth - 300 }}
            PaperProps={{
              sx: {
                position: "fixed",
                top: 10,
                right: 10,
                width: 280,
                boxShadow: 3,
              },
            }}
          >
            <Box>
              <Table sx={{ minWidth: 250 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>Tests</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>Count</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.values(chart?.data || {}).map((entry, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: "50%",
                              backgroundColor:
                                PIE_COLORS[index % PIE_COLORS.length],
                            }}
                          />
                          {entry.name}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        {entry.value.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Popover>
        ))}
      </Box>
    );
  };
  console.log(selectedFilter);
  const [index, setIndex] = useState(0);

  const handleSelect = (selectedIndex) => {
    setIndex(selectedIndex);
  };
  return (
    // <Box sx={{ backgroundColor: "#f9fafb", minHeight: "100vh" }}>
    //   {/* Filter Buttons */}
    //   {/* <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
    //     <AlertTitle>Dashboard In Development</AlertTitle>
    //     This dashboard is currently under development. The data displayed here
    //     may not reflect actual figures.
    //   </Alert> */}
    //   <Box sx={{ mb: 3, display: "flex", flexWrap: "wrap", gap: 1.5 }}>
    //     {filterOptions.map((filter) => (
    //       <Button
    //         key={filter.value}
    //         variant="outlined"
    //         size="small"
    //         onClick={() => {
    //           setSelectedFilter(filter);
    //           setRangeDate(false);
    //         }}
    //         disabled={loading}
    //         sx={{
    //           display:
    //             filter.label == "Today"
    //               ? "block"
    //               : user?._doc?.permissions?.dashboardManagement?.view == true
    //                 ? "block"
    //                 : "none",
    //           borderRadius: 2,
    //           textTransform: "none",
    //           backgroundColor:
    //             selectedFilter?.value === filter?.value ? "#fde047" : "#fff",
    //           color:
    //             selectedFilter?.value === filter?.value ? "#000" : "#1f2937",
    //           borderColor:
    //             selectedFilter?.value === filter?.value ? "#facc15" : "#d1d5db",
    //         }}
    //       >
    //         {filter.label}
    //       </Button>
    //     ))}
    //     {user?._doc?.permissions?.dashboardManagement?.view && (
    //       <div
    //         style={{
    //           display: "flex",
    //           gap: "10px",
    //           marginLeft: "auto",
    //         }}
    //       >
    //         <input
    //           type="date"
    //           value={startDate}
    //           onChange={(e) => setStartDate(e.target.value)}
    //           max={endDate}
    //           disabled={loading}
    //           style={{
    //             borderRadius: "8px",
    //             border: "1px solid #d1d5db",
    //             padding: "0px 5px",
    //             fontSize: "14px",
    //             backgroundColor: "#fff",
    //           }}
    //         />
    //         <button
    //           style={{
    //             borderRadius: "8px",
    //             backgroundColor: rangeDate ? "#fde047" : "#fff",
    //             color: "#000",
    //             border: "1px solid #d1d5db",
    //             outline: "none",
    //             padding: "0px 5px",
    //             position: "relative",
    //           }}
    //           onClick={getRangeData}
    //         >
    //           <ArrowRight />
    //           <span
    //             style={{ position: "absolute", bottom: "-8px", left: "22px" }}
    //           >
    //             🔍︎
    //           </span>
    //         </button>
    //         <input
    //           type="date"
    //           value={endDate}
    //           onChange={(e) => setEndDate(e.target.value)}
    //           min={startDate}
    //           disabled={loading}
    //           style={{
    //             borderRadius: "8px",
    //             border: "1px solid #d1d5db",
    //             padding: "0px 5px",
    //             fontSize: "14px",
    //             backgroundColor: "#fff",
    //           }}
    //         />
    //       </div>
    //     )}
    //   </Box>
    //   <Box mb={2}>
    //     <Grid container spacing={2}>
    //       <Grid item xs={12} sm={6} md={3}>
    //         <Paper sx={OverallCardStyle}>
    //           <Box display="flex" alignItems="center" gap={2}>
    //             {loading ? (
    //               <Skeleton variant="circular" width={40} height={40} />
    //             ) : (
    //               React.createElement(getIcon("Total Invoices"), {
    //                 fontSize: "large",
    //                 sx: { color: "#fb923c" },
    //               })
    //             )}
    //             <Box>
    //               {loading ? (
    //                 <Skeleton width={120} />
    //               ) : (
    //                 <Typography
    //                   variant="subtitle2"
    //                   sx={{ fontFamily: "Orbitron" }}
    //                 >
    //                   Invoices
    //                 </Typography>
    //               )}
    //               <Typography variant="h6" fontWeight="bold">
    //                 {loading ? (
    //                   <Skeleton width={60} />
    //                 ) : (
    //                   <CountUp
    //                     end={dashInfo?.totalInvoices}
    //                     duration={1.5}
    //                     formattingFn={indianFormatter}
    //                     style={{ fontFamily: "Orbitron" }}
    //                   />
    //                 )}
    //               </Typography>
    //             </Box>
    //           </Box>
    //         </Paper>
    //       </Grid>
    //       <Grid item xs={12} sm={6} md={3}>
    //         <Paper sx={OverallCardStyle}>
    //           <Box display="flex" alignItems="center" gap={2}>
    //             {loading ? (
    //               <Skeleton variant="circular" width={40} height={40} />
    //             ) : (
    //               React.createElement(getIcon("Total Revenue"), {
    //                 fontSize: "large",
    //                 sx: { color: "#34d399" },
    //               })
    //             )}
    //             <Box>
    //               {loading ? (
    //                 <Skeleton width={120} />
    //               ) : (
    //                 <Typography
    //                   variant="subtitle2"
    //                   sx={{ fontFamily: "Orbitron" }}
    //                 >
    //                   Revenue
    //                 </Typography>
    //               )}
    //               <Typography variant="h6" fontWeight="bold">
    //                 {loading ? (
    //                   <Skeleton width={60} />
    //                 ) : (
    //                   <CountUp
    //                     end={dashInfo?.totalRevenue}
    //                     duration={1.5}
    //                     formattingFn={indianFormatter}
    //                     style={{ fontFamily: "Orbitron" }}
    //                   />
    //                 )}
    //               </Typography>
    //             </Box>
    //           </Box>
    //         </Paper>
    //       </Grid>
    //       <Grid item xs={12} sm={6} md={3}>
    //         <Paper sx={OverallCardStyle}>
    //           <Box display="flex" alignItems="center" gap={2}>
    //             {loading ? (
    //               <Skeleton variant="circular" width={40} height={40} />
    //             ) : (
    //               React.createElement(getIcon("Total Balance Due"), {
    //                 fontSize: "large",
    //                 sx: { color: "#f87171" },
    //               })
    //             )}
    //             <Box>
    //               {loading ? (
    //                 <Skeleton width={120} />
    //               ) : (
    //                 <Typography
    //                   variant="subtitle2"
    //                   sx={{ fontFamily: "Orbitron" }}
    //                 >
    //                   Balance Due
    //                 </Typography>
    //               )}
    //               <Typography variant="h6" fontWeight="bold">
    //                 {loading ? (
    //                   <Skeleton width={60} />
    //                 ) : (
    //                   <CountUp
    //                     end={dashInfo?.totalDue || 0}
    //                     duration={1.5}
    //                     formattingFn={indianFormatter}
    //                     preserveValue
    //                     style={{ fontFamily: "Orbitron" }}
    //                   />
    //                 )}
    //               </Typography>
    //             </Box>
    //           </Box>
    //         </Paper>
    //       </Grid>
    //       <Grid item xs={12} sm={6} md={3}>
    //         <Paper sx={OverallCardStyle}>
    //           <Box display="flex" alignItems="center" gap={2}>
    //             {loading ? (
    //               <Skeleton variant="circular" width={40} height={40} />
    //             ) : (
    //               React.createElement(getIcon("Total Payments Collected"), {
    //                 fontSize: "large",
    //                 sx: { color: "#60a5fa" },
    //               })
    //             )}
    //             <Box>
    //               {loading ? (
    //                 <Skeleton width={120} />
    //               ) : (
    //                 <Typography
    //                   variant="subtitle2"
    //                   sx={{ fontFamily: "Orbitron" }}
    //                 >
    //                   Payment Collected
    //                 </Typography>
    //               )}
    //               <Typography variant="h6" fontWeight="bold">
    //                 {loading ? (
    //                   <Skeleton width={60} />
    //                 ) : (
    //                   <CountUp
    //                     end={dashInfo?.totalCollected || 0}
    //                     duration={1.5}
    //                     formattingFn={indianFormatter}
    //                     style={{ fontFamily: "Orbitron" }}
    //                   />
    //                 )}
    //               </Typography>
    //             </Box>
    //           </Box>
    //         </Paper>
    //       </Grid>
    //     </Grid>
    //   </Box>
    //   {user?._doc?.permissions?.dashboardManagement?.view && renderChartCard()}

    //   {user?._doc?.permissions?.dashboardManagement?.view && (
    //     <Grid container spacing={2}>
    //       <Grid item xs={12} sm={12}>
    //         <Paper
    //           elevation={3}
    //           sx={{
    //             width: "100%",
    //             height: 400,
    //             p: 2,
    //             borderRadius: 3,
    //             backgroundColor: "#ffffff",
    //             boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    //             display: "flex",
    //             flexDirection: "column",
    //             justifyContent: "center",
    //           }}
    //         >
    //           {/* Header */}
    //           <Box
    //             sx={{
    //               display: "flex",
    //               justifyContent: "space-between",
    //               alignItems: "center",
    //             }}
    //           >
    //             <Typography
    //               variant="h6"
    //               sx={{
    //                 fontWeight: 600,
    //                 mb: 2,
    //                 color: "#111827",
    //                 fontFamily: "Inter, Arial, sans-serif",
    //               }}
    //             >
    //               Doctor-wise Test Counts
    //             </Typography>
    //             <Chip
    //               label={
    //                 selectedFilter
    //                   ? selectedFilter.label
    //                   : `${new Date(startDate)
    //                       .toLocaleDateString("en-GB", {
    //                         day: "numeric",
    //                         month: "short",
    //                         year: "2-digit",
    //                       })
    //                       .replace(" ", "'")} To ${new Date(endDate)
    //                       .toLocaleDateString("en-GB", {
    //                         day: "numeric",
    //                         month: "short",
    //                         year: "2-digit",
    //                       })
    //                       .replace(" ", "'")}`
    //               }
    //             />
    //           </Box>
    //           {/* Chart */}
    //           <Box sx={{ flex: 1 }}>
    //             <ResponsiveContainer width="100%" height="100%">
    //               <BarChart data={doctorWiseTestsData}>
    //                 <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
    //                 <XAxis
    //                   dataKey="doctor"
    //                   tick={{ fontSize: 12, fill: "#555" }}
    //                 />
    //                 <YAxis tick={{ fontSize: 12, fill: "#555" }} />
    //                 <Tooltip content={<CustomTooltipStack />} />
    //                 {testTypes.map((test, idx) => (
    //                   <Bar
    //                     key={test}
    //                     dataKey={test} // string key now, tooltip will work
    //                     stackId="a"
    //                     fill={PIE_COLORS[idx % PIE_COLORS.length]}
    //                   />
    //                 ))}
    //               </BarChart>
    //             </ResponsiveContainer>
    //           </Box>
    //         </Paper>
    //       </Grid>
    //     </Grid>
    //   )}
    // </Box>

    <Box
      sx={{
        backgroundColor: "#f4f6fb",
        minHeight: "100vh",
        // p: 1,
      }}
    >
      {/* ── Filter Bar ── */}
      <Box
        sx={{
          mb: 3,
          background: "#ffffff",
          border: "1px solid #e5e9ef",
          borderRadius: "14px",
          px: 2,
          py: 1.5,
          boxShadow: "0 2px 8px rgba(10,31,68,0.06)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        {/* ── Left: Period label + filter buttons ── */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            flexWrap: "wrap",
          }}
        >
          <Typography
            sx={{
              fontSize: "0.62rem",
              fontWeight: 700,
              color: "#94a3b8",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              mr: 0.5,
              flexShrink: 0,
            }}
          >
            Period
          </Typography>

          {filterOptions.map((filter) => (
            <Button
              key={filter.value}
              size="small"
              onClick={() => {
                setSelectedFilter(filter);
                setRangeDate(false);
              }}
              disabled={loading}
              sx={{
                display:
                  filter.label === "Today"
                    ? "inline-flex"
                    : user?._doc?.permissions?.dashboardManagement?.view
                      ? "inline-flex"
                      : "none",
                borderRadius: "8px",
                textTransform: "none",
                fontSize: "0.75rem",
                fontWeight: selectedFilter?.value === filter?.value ? 700 : 500,
                px: 1.5,
                py: 0.5,
                minWidth: "auto",
                flexShrink: 0,
                border: "1.5px solid",
                borderColor:
                  selectedFilter?.value === filter?.value
                    ? "transparent"
                    : "#e5e9ef",
                background:
                  selectedFilter?.value === filter?.value
                    ? "linear-gradient(135deg, #2a54a6, #b93acf)"
                    : "transparent",
                color:
                  selectedFilter?.value === filter?.value
                    ? "#ffffff"
                    : "#64748b",
                boxShadow:
                  selectedFilter?.value === filter?.value
                    ? "0 3px 10px rgba(185,58,207,0.25)"
                    : "none",
                "&:hover": {
                  background:
                    selectedFilter?.value === filter?.value
                      ? "linear-gradient(135deg, #1e3d8a, #a02db8)"
                      : "rgba(42,84,166,0.06)",
                  borderColor:
                    selectedFilter?.value === filter?.value
                      ? "transparent"
                      : "rgba(42,84,166,0.25)",
                  color:
                    selectedFilter?.value === filter?.value
                      ? "#ffffff"
                      : "#2a54a6",
                },
                transition: "all 0.2s ease",
              }}
            >
              {filter.label}
            </Button>
          ))}
        </Box>

        {/* ── Right: Custom date range (only for admin) ── */}
        {user?._doc?.permissions?.dashboardManagement?.view && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              flexShrink: 0,
              flexWrap: "nowrap",
            }}
          >
            <Typography
              sx={{
                fontSize: "0.62rem",
                fontWeight: 700,
                color: "#94a3b8",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                flexShrink: 0,
              }}
            >
              Custom
            </Typography>

            <Box
              component="input"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              max={endDate}
              disabled={loading}
              sx={{
                borderRadius: "8px",
                border: "1.5px solid #e5e9ef",
                padding: "5px 8px",
                fontSize: "0.75rem",
                color: "#0a1f44",
                background: "#f8fafc",
                fontFamily: "inherit",
                outline: "none",
                cursor: "pointer",
                width: "130px",
                "&:focus": { borderColor: "#b93acf", background: "#fff" },
                transition: "border-color 0.2s",
              }}
            />

            <Box
              component="button"
              onClick={getRangeData}
              disabled={loading}
              sx={{
                borderRadius: "8px",
                background: rangeDate
                  ? "linear-gradient(135deg, #2a54a6, #b93acf)"
                  : "#ffffff",
                color: rangeDate ? "#ffffff" : "#2a54a6",
                border: "1.5px solid",
                borderColor: rangeDate ? "transparent" : "rgba(42,84,166,0.3)",
                outline: "none",
                px: 1.25,
                py: "5px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                fontSize: "0.75rem",
                fontWeight: 600,
                flexShrink: 0,
                boxShadow: rangeDate
                  ? "0 3px 10px rgba(185,58,207,0.25)"
                  : "none",
                "&:hover": {
                  background: "linear-gradient(135deg, #2a54a6, #b93acf)",
                  color: "#ffffff",
                  borderColor: "transparent",
                  boxShadow: "0 3px 10px rgba(185,58,207,0.25)",
                },
                transition: "all 0.2s ease",
              }}
            >
              <ArrowRight sx={{ fontSize: "0.95rem" }} />
              <span>🔍</span>
            </Box>

            <Box
              component="input"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate}
              disabled={loading}
              sx={{
                borderRadius: "8px",
                border: "1.5px solid #e5e9ef",
                padding: "5px 8px",
                fontSize: "0.75rem",
                color: "#0a1f44",
                background: "#f8fafc",
                fontFamily: "inherit",
                outline: "none",
                cursor: "pointer",
                width: "130px",
                "&:focus": { borderColor: "#b93acf", background: "#fff" },
                transition: "border-color 0.2s",
              }}
            />
          </Box>
        )}
      </Box>

      {/* ── Stat Cards ── */}
      <Box mb={2.5}>
        <Grid container spacing={2}>
          {[
            {
              key: "totalInvoices",
              label: "Invoices",
              iconKey: "Total Invoices",
              iconColor: "#f97316",
              gradientFrom: "rgba(249,115,22,0.12)",
              gradientTo: "rgba(249,115,22,0.04)",
              borderColor: "rgba(249,115,22,0.2)",
            },
            {
              key: "totalRevenue",
              label: "Revenue",
              iconKey: "Total Revenue",
              iconColor: "#10b981",
              gradientFrom: "rgba(16,185,129,0.12)",
              gradientTo: "rgba(16,185,129,0.04)",
              borderColor: "rgba(16,185,129,0.2)",
            },
            {
              key: "totalDue",
              label: "Balance Due",
              iconKey: "Total Balance Due",
              iconColor: "#ef4444",
              gradientFrom: "rgba(239,68,68,0.12)",
              gradientTo: "rgba(239,68,68,0.04)",
              borderColor: "rgba(239,68,68,0.2)",
            },
            {
              key: "totalCollected",
              label: "Payment Collected",
              iconKey: "Total Payments Collected",
              iconColor: "#2a54a6",
              gradientFrom: "rgba(42,84,166,0.12)",
              gradientTo: "rgba(42,84,166,0.04)",
              borderColor: "rgba(42,84,166,0.2)",
            },
          ].map((card) => (
            <Grid item xs={12} sm={6} md={3} key={card.key}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: "16px",
                  background: "#ffffff",
                  border: `1.5px solid ${card.borderColor}`,
                  boxShadow: "0 2px 10px rgba(10,31,68,0.06)",
                  transition: "all 0.25s ease",
                  position: "relative",
                  overflow: "hidden",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 8px 24px rgba(10,31,68,0.1)",
                  },
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "3px",
                    background: `linear-gradient(90deg, ${card.iconColor}, transparent)`,
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    {loading ? (
                      <Skeleton width={110} height={16} sx={{ mb: 1 }} />
                    ) : (
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          fontWeight: 600,
                          color: "#94a3b8",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          mb: 0.5,
                        }}
                      >
                        {card.label}
                      </Typography>
                    )}
                    <Typography
                      sx={{
                        fontFamily: "Orbitron, monospace",
                        fontWeight: 700,
                        fontSize: "1.35rem",
                        color: "#0a1f44",
                        lineHeight: 1.2,
                      }}
                    >
                      {loading ? (
                        <Skeleton width={80} height={28} />
                      ) : (
                        <CountUp
                          end={dashInfo?.[card.key] || 0}
                          duration={1.5}
                          formattingFn={indianFormatter}
                          preserveValue
                        />
                      )}
                    </Typography>
                  </Box>

                  {/* Icon bubble */}
                  {loading ? (
                    <Skeleton variant="circular" width={44} height={44} />
                  ) : (
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: "12px",
                        background: `linear-gradient(135deg, ${card.gradientFrom}, ${card.gradientTo})`,
                        border: `1.5px solid ${card.borderColor}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {React.createElement(getIcon(card.iconKey), {
                        sx: { color: card.iconColor, fontSize: "1.3rem" },
                      })}
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* ── Charts ── */}
      {user?._doc?.permissions?.dashboardManagement?.view && renderChartCard()}

      {/* ── Doctor-wise Bar Chart ── */}
      {user?._doc?.permissions?.dashboardManagement?.view && (
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                width: "100%",
                p: 2.5,
                borderRadius: "16px",
                background: "#ffffff",
                border: "1px solid #e5e9ef",
                boxShadow: "0 2px 10px rgba(10,31,68,0.06)",
                display: "flex",
                flexDirection: "column",
                height: 420,
              }}
            >
              {/* Card header */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "9px",
                      background: "linear-gradient(135deg, #2a54a6, #b93acf)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <BarChartIcon sx={{ color: "#fff", fontSize: "1rem" }} />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.92rem",
                        color: "#0a1f44",
                        lineHeight: 1.2,
                      }}
                    >
                      Doctor-wise Test Counts
                    </Typography>
                    <Typography
                      sx={{ fontSize: "0.68rem", color: "#94a3b8", mt: "1px" }}
                    >
                      Stacked by test type
                    </Typography>
                  </Box>
                </Box>

                <Chip
                  label={
                    selectedFilter
                      ? selectedFilter.label
                      : `${new Date(startDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "2-digit" }).replace(" ", "'")} → ${new Date(endDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "2-digit" }).replace(" ", "'")}`
                  }
                  size="small"
                  sx={{
                    background:
                      "linear-gradient(135deg, rgba(42,84,166,0.1), rgba(185,58,207,0.08))",
                    border: "1px solid rgba(185,58,207,0.2)",
                    color: "#2a54a6",
                    fontWeight: 700,
                    fontSize: "0.7rem",
                    height: 26,
                  }}
                />
              </Box>

              {/* Chart */}
              <Box sx={{ flex: 1 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={doctorWiseTestsData}
                    margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#f1f5f9"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="doctor"
                      tick={{
                        fontSize: 11,
                        fill: "#94a3b8",
                        fontFamily: "inherit",
                      }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{
                        fontSize: 11,
                        fill: "#94a3b8",
                        fontFamily: "inherit",
                      }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      content={<CustomTooltipStack />}
                      cursor={{ fill: "rgba(42,84,166,0.04)" }}
                    />
                    {testTypes.map((test, idx) => (
                      <Bar
                        key={test}
                        dataKey={test}
                        stackId="a"
                        fill={PIE_COLORS[idx % PIE_COLORS.length]}
                        radius={
                          idx === testTypes.length - 1
                            ? [4, 4, 0, 0]
                            : [0, 0, 0, 0]
                        }
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default DashboardInformation;
